import { Form, FormItemProps, Select, SelectProps } from "antd";
import styles from "./CustomStoreSelect.module.scss";
import useFetchInfinity from "../../hooks/useFetchInfinity";
import { MODES } from "../../constants/user";
import { useContext, useEffect, useMemo, useState } from "react";
import { AppContext } from "../../../contexts/AppContext";
import { MERCHANT_STORES_URL } from "../../constants/api-urls";
import { debounce } from "lodash";
import { STORE_TYPE_TABS } from "../../../components/StoreManagement";
import { STORE_TYPE } from "../../constants/store";

interface Props extends SelectProps {
  name?: string | (string | number)[];
  label?: React.ReactNode;
  rules?: FormItemProps["rules"];
  selectClassName?: string;
  prefixIcon?: React.ReactNode;
  labelValue?: string;
  storeType?: STORE_TYPE_TABS;
  disabled?: boolean;
}

export const CustomStoreSelect = (props: Props) => {
  const {
    defaultValue,
    name,
    rules,
    label,
    className,
    selectClassName,
    prefixIcon,
    labelValue = "",
    storeType = STORE_TYPE_TABS.ALL,
    disabled = false,
    ...selectProps
  } = props;
  const { user } = useContext(AppContext);
  const [keyword, setKeyword] = useState("");
  const [searchValue, setSearchValue] = useState("");

  const storeTypeForParams = useMemo(() => {
    switch (storeType) {
      case STORE_TYPE_TABS.FOOD_DELIVERY:
        return STORE_TYPE.FOOD_DELIVERY;
      case STORE_TYPE_TABS.SURPLUS:
        return STORE_TYPE.SURPLUS;
      default:
        return undefined;
    }
  }, [storeType]);

  const { data, hasMore, next, loading } = useFetchInfinity<Store>(
    user?.merchantIds && Number(user?.merchant_role?.id) === MODES.ADMIN
      ? MERCHANT_STORES_URL(user.merchantIds[0])
      : "",
    {
      type: storeTypeForParams,
      keyword: keyword.trim(),
      pageSize: 15,
      perPage: 15,
    }
  );

  const optionsData = useMemo(() => {
    if (!data || data.length === 0) return [];

    return data.map((store: Store) => ({
      value: store.id,
      label: store.name,
    }));
  }, [data]);


  const handlePopupScroll = (e: any) => {
    const { scrollTop, offsetHeight, scrollHeight } = e.target;
    if (!loading && hasMore && scrollTop + offsetHeight >= scrollHeight - 50) {
      next();
    }
  };

  const handleSearch = (e: string) => {
    const value = e;
    setSearchValue(value);
    handleFetchData(value);
  };

  const handleClearValue = () => {
    setSearchValue("");
    setKeyword("");
  };

  const handleFetchData = debounce(data => {
    setKeyword(data);
  }, 500);

  useEffect(() => {
    if (labelValue) {
      setKeyword(labelValue);
      setSearchValue(labelValue);
    }
  }, [labelValue]);

  return (
    <Form.Item className={`${styles.wrapper} ${className ?? "input"}`}>
      {prefixIcon && <div className={styles["prefix-icon-wrapper"]}>{prefixIcon}</div>}
      <Form.Item
        name={name}
        className={`${styles.group} ${label ? styles.label : ""}`}
        label={label}
        rules={rules}
        labelCol={{ span: 24 }}
      >
        <Select
          className={`${styles.customInput} ${prefixIcon && styles.customIcon} ${selectClassName ?? ""} ${
            disabled ? styles.disabled : ""
          }`}
          {...selectProps}
          onPopupScroll={handlePopupScroll}
          showSearch
          filterOption={false}
          options={optionsData}
          onSearch={(value: string) => handleSearch(value)}
          allowClear
          searchValue={searchValue}
          disabled={disabled}
          onClear={handleClearValue}
        />
      </Form.Item>
    </Form.Item>
  );
};
